import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { ZonesService } from '../../../shared/services/zones.service';
import {
  ZONE_TYPE_LABEL,
  ZONE_TYPE_OPTIONS,
  Zone,
  ZoneType,
} from '../../../shared/types/zone.types';

type FormMode = 'create' | 'edit';

const ZONE_TYPE_PRETTY: PrettySelectOption<ZoneType>[] = ZONE_TYPE_OPTIONS.map((o) => ({
  value: o.value,
  label: o.label,
}));

@Component({
  selector: 'app-zones-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PageHeaderComponent,
    DataStateComponent,
    ModalComponent,
    ConfirmModalComponent,
    PrettySelectComponent,
  ],
  templateUrl: './zones-list.component.html',
})
export class ZonesListComponent implements OnInit {
  private readonly service = inject(ZonesService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly items = signal<Zone[]>([]);

  readonly typeLabel = ZONE_TYPE_LABEL;
  readonly typeOptions = ZONE_TYPE_PRETTY;

  // ── Edit / create modal ──────────────────────────────────────────────
  readonly editOpen = signal(false);
  readonly editMode = signal<FormMode>('create');
  /** Current target code in edit mode; `null` in create mode. */
  readonly editCode = signal<string | null>(null);
  readonly editSaving = signal(false);
  readonly editError = signal<string | null>(null);
  readonly editForm = this.fb.group({
    code: ['', [Validators.required, Validators.maxLength(50)]],
    name: ['', [Validators.required, Validators.maxLength(100)]],
    zoneType: ['CITY' as ZoneType, [Validators.required]],
  });

  // ── Polygon modal ────────────────────────────────────────────────────
  readonly polyOpen = signal(false);
  readonly polyCode = signal<string | null>(null);
  readonly polySaving = signal(false);
  readonly polyError = signal<string | null>(null);
  readonly polyForm = this.fb.group({
    geoJsonPolygon: ['', [Validators.required]],
  });

  // ── Delete confirm ───────────────────────────────────────────────────
  readonly deleteOpen = signal(false);
  readonly deleteCode = signal<string | null>(null);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.items.set(
            [...(rows ?? [])].sort((a, b) => a.code.localeCompare(b.code)),
          );
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.items.set([]);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  // ── Create / Edit ─────────────────────────────────────────────────────
  openCreate(): void {
    this.editMode.set('create');
    this.editCode.set(null);
    this.editForm.reset({ code: '', name: '', zoneType: 'CITY' });
    this.editForm.controls.code.enable({ emitEvent: false });
    this.editError.set(null);
    this.editOpen.set(true);
  }
  openEdit(z: Zone): void {
    this.editMode.set('edit');
    this.editCode.set(z.code);
    this.editForm.reset({ code: z.code, name: z.name, zoneType: z.zoneType });
    this.editForm.controls.code.disable({ emitEvent: false });
    this.editError.set(null);
    this.editOpen.set(true);
  }
  closeEdit(): void {
    if (this.editSaving()) return;
    this.editOpen.set(false);
  }
  submitEdit(): void {
    if (this.editForm.invalid || this.editSaving()) {
      this.editForm.markAllAsTouched();
      return;
    }
    const raw = this.editForm.getRawValue();
    this.editSaving.set(true);
    this.editError.set(null);
    const op =
      this.editMode() === 'create'
        ? this.service.create({
            code: raw.code.trim(),
            name: raw.name.trim(),
            zoneType: raw.zoneType,
          })
        : this.service.update(this.editCode()!, {
            name: raw.name.trim(),
            zoneType: raw.zoneType,
          });
    op.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.editSaving.set(false);
        this.editOpen.set(false);
        this.load();
      },
      error: (err: unknown) => {
        this.editSaving.set(false);
        this.editError.set(
          toErrorMessage(err, "L'enregistrement a échoué.", {
            409: 'Ce code de zone existe déjà.',
          }),
        );
      },
    });
  }

  // ── Polygon ──────────────────────────────────────────────────────────
  openPolygon(z: Zone): void {
    this.polyCode.set(z.code);
    this.polyForm.reset({ geoJsonPolygon: z.polygon ?? '' });
    this.polyError.set(null);
    this.polyOpen.set(true);
  }
  closePolygon(): void {
    if (this.polySaving()) return;
    this.polyOpen.set(false);
  }
  submitPolygon(): void {
    const code = this.polyCode();
    if (!code || this.polyForm.invalid || this.polySaving()) {
      this.polyForm.markAllAsTouched();
      return;
    }
    this.polySaving.set(true);
    this.polyError.set(null);
    this.service
      .setPolygon(code, { geoJsonPolygon: this.polyForm.controls.geoJsonPolygon.value })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.polySaving.set(false);
          this.polyOpen.set(false);
          this.load();
        },
        error: (err: unknown) => {
          this.polySaving.set(false);
          this.polyError.set(
            toErrorMessage(err, "L'enregistrement a échoué.", {
              400: 'Le polygone GeoJSON est invalide.',
            }),
          );
        },
      });
  }

  // ── Delete ───────────────────────────────────────────────────────────
  openDelete(z: Zone): void {
    this.deleteCode.set(z.code);
    this.deleteError.set(null);
    this.deleteOpen.set(true);
  }
  closeDelete(): void {
    if (this.deleting()) return;
    this.deleteOpen.set(false);
  }
  confirmDelete(): void {
    const code = this.deleteCode();
    if (!code) return;
    this.deleting.set(true);
    this.deleteError.set(null);
    this.service
      .delete(code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleting.set(false);
          this.deleteOpen.set(false);
          this.load();
        },
        error: (err: unknown) => {
          this.deleting.set(false);
          this.deleteError.set(
            toErrorMessage(err, 'errors.deleteFailed', {
              409: "Des opérateurs référencent encore cette zone — déréférence-les d'abord.",
            }),
          );
        },
      });
  }
}
